
import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenAI } from "@google/genai";

interface Message {
  role: 'user' | 'assistant';
  content: string;
  groundingLinks?: { title: string; uri: string }[];
  showCityMap?: boolean;
  cityForMap?: string;
}

interface AiAgentPageProps {
  isRTL: boolean;
  language: string;
  onBack: () => void;
  onHome: () => void;
}

const AiAgentPage: React.FC<AiAgentPageProps> = ({ isRTL, language, onBack, onHome }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionCity, setSessionCity] = useState<string | null>(null);
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const tasksMapping: Record<string, string[]> = {
    zh: ['SSN', '银行', '手机卡', '驾照', '保险', '超市', '就医', '租车'],
    en: ['SSN', 'Banking', 'SIM Card', 'Driver\'s License', 'Insurance', 'Supermarket', 'Healthcare', 'Car Rental'],
    default: ['SSN', 'Banking', 'SIM Card', 'Driver\'s License', 'Insurance', 'Supermarket', 'Healthcare', 'Car Rental']
  };

  const currentTasks = tasksMapping[language] || tasksMapping.default;

  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMessages: Record<string, string> = {
        en: "Hi! Hello. I am Settlo AI. I can assist you in understanding the details of settling in US cities. Please first provide the name of the US city or zip code you are interested in.",
        zh: "嗨！您好。我是 Settlo Ai。我可以协助你了解更多美国城市定居的细节，请先提供您感兴趣的美国城市名称或邮政编码。",
      };
      setMessages([{ 
        role: 'assistant', 
        content: welcomeMessages[language] || welcomeMessages.en
      }]);
    }
  }, [language]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, loading]);

  const handleSend = async (text: string) => {
    if (!text.trim() || loading) return;
    const userMsg: Message = { role: 'user', content: text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    const matchedTask = currentTasks.find(t => text.toLowerCase().includes(t.toLowerCase()));
    if (matchedTask) {
      setCompletedTasks(prev => new Set(prev).add(matchedTask));
    }

    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const targetLang = language === 'zh' ? 'Chinese' : 'English';

      const systemInstruction = `You are Settlo Ai, the world's leading US Relocation Expert. You are professional, deeply knowledgeable, and RADIATE WARMTH AND ENTHUSIASM.

STRICT OPERATIONAL DIRECTIVES:

0. AMBIGUITY CHECK (CRITICAL):
   - If a user provides a city name that exists in multiple states (e.g., Springfield, Portland, Columbus) and the state is NOT confirmed, you MUST ask: "Which [City Name] are you interested in?"
   - Do not provide a profile or map until the specific location is confirmed.
   - Provide the options as selection bubbles using: [BUBBLES: City, State X; City, State Y; ...] at the end. Use SEMICOLON (;) as the separator.

1. FIRST CITY RECOGNITION (Once Confirmed):
   - Provide a expert, high-level profile of the city.
   - IMMEDIATELY after the profile text, you MUST include the exact tag [TRIGGERMAP] on its own line.
   - Then, PROACTIVELY guide the user: "In [City], the most critical relocation tasks are... Which one should we tackle first? I'm so excited to help you get settled!"
   - Always include task suggestions using [BUBBLES: task1; task2; ...] at the end. Use SEMICOLON (;) as the separator.

2. EVERY RESPONSE REQUIREMENT:
   - Provide expert, structured, and concise answers using segment headers.
   - CRITICAL: At the end of EVERY answer, you MUST be extremely warm and proactively guide the user to the next step. 
   - Examples: "I'm here for you every step of the way! Let's get everything ready for your new life in [City]. What would you like to explore next?"
   - ALWAYS provide new bubbles for remaining tasks using [BUBBLES: taskA; taskB; ...].

3. FORMATTING FOR ELEGANCE:
   - Use colons for all segment headers (e.g., "Housing: " or "办理地点："). 
   - Headers should be at the start of paragraphs to ensure proper bolding.
   - DO NOT use markdown symbols like * or _. Use plain text and colons.

4. LANGUAGE: Respond strictly in ${targetLang}.

CONTEXT:
- Session City: ${sessionCity || 'Not yet confirmed'}
- Completed: ${Array.from(completedTasks).join(', ')}
- Remaining: ${currentTasks.filter(t => !completedTasks.has(t)).join(', ')}`;

      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: updatedMessages.slice(-10).map(m => ({
          role: m.role === 'user' ? 'user' : 'model',
          parts: [{ text: m.content }]
        })),
        config: { 
          systemInstruction,
          tools: [{ googleSearch: {} }] 
        }
      });

      let responseText = response.text || '';
      
      let detectedBubbles: string[] = [];
      const bubbleMatch = responseText.match(/\[BUBBLES:\s*([^\]]+)\]/i);
      if (bubbleMatch) {
        detectedBubbles = bubbleMatch[1].split(';').map(s => s.trim()).filter(Boolean);
      }

      let detectedCity = sessionCity;
      const confirmedCityMatch = responseText.match(/In\s+([^,]+(?:,\s*[A-Z]{2,}|,\s*[\u4e00-\u9fa5]+)?),\s+the\s+most\s+critical/i);
      const introMatch = responseText.match(/(?:welcome to|about|overview of|选择|纽约|洛杉矶|芝加哥|休斯顿|凤凰城|费城|圣安东尼奥|圣地亚哥|达拉斯|圣何塞|New York|Los Angeles|Chicago|Houston|Phoenix|Philadelphia|San Antonio|San Diego|Dallas|San Jose|Austin|Seattle|Boston|Miami|San Francisco|Denver|Atlanta|Las Vegas|Orlando|Location Profile|Apex)\s*[:：]?\s*([A-Za-z\u4e00-\u9fa5\s,]+)/i);
      
      if (confirmedCityMatch) {
          detectedCity = confirmedCityMatch[1].trim();
          setSessionCity(detectedCity);
      } else if (!sessionCity && introMatch) {
          detectedCity = introMatch[1].trim().split(/[,\n]/)[0];
          if (!responseText.includes('?') || /\[TRIGGERMAP\]/i.test(responseText)) {
             setSessionCity(detectedCity);
          }
      }

      const links: { title: string; uri: string }[] = [];
      const chunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
      chunks.forEach((chunk: any) => {
        const uri = chunk?.web?.uri;
        const title = chunk?.web?.title || 'Resource';
        if (uri) links.push({ title, uri });
      });

      const hasMapTrigger = /\[TRIGGERMAP\]/i.test(responseText);
      
      const cleanedText = responseText
        .replace(/\[BUBBLES:\s*[^\]]+\]/gi, '')
        .replace(/\*/g, '')
        .replace(/_/g, '')
        .trim();

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: cleanedText,
        groundingLinks: links.slice(0, 5),
        showCityMap: hasMapTrigger,
        cityForMap: detectedCity || undefined
      }]);

      setSuggestions(detectedBubbles);
      
    } catch (err) {
      console.error("AI Error:", err);
      setMessages(prev => [...prev, { role: 'assistant', content: language === 'zh' ? "抱歉，由于网络波动，我没能跟上您的步调。请再说一次？" : "Apologies, I hit a slight connection snag. Could you repeat that?" }]);
    } finally {
      setLoading(false);
    }
  };

  const renderTextContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={i} className="h-4" />;
      
      // Robust header detection for bolding (colon at start of segment)
      const colonMatch = trimmed.match(/[:：]/);
      if (colonMatch) {
        const colonIndex = trimmed.indexOf(colonMatch[0]);
        // Headers are typically short; colons appearing after 60 chars are likely content
        if (colonIndex > 0 && colonIndex < 60) {
          const heading = trimmed.substring(0, colonIndex + 1);
          const body = trimmed.substring(colonIndex + 1).trim();
          return (
            <div key={i} className="mb-5 last:mb-0">
              <div className="font-[900] text-slate-dark text-[17px] leading-tight mb-2 tracking-tight">
                {heading}
              </div>
              {body && <p className="text-slate-soft text-[15px] leading-relaxed">{body}</p>}
            </div>
          );
        }
      }
      
      return (
        <p key={i} className="mb-4 last:mb-0 text-slate-soft text-[15px] leading-relaxed">
          {trimmed}
        </p>
      );
    });
  };

  const renderAssistantMessage = (msg: Message) => {
    const parts = msg.content.split(/\[TRIGGERMAP\]/i);

    return (
      <div className="flex flex-col w-full font-google-sans-text">
        <div className="w-full">
          {renderTextContent(parts[0])}
        </div>

        {msg.showCityMap && msg.cityForMap && (
          <div className="my-10 w-full rounded-stripe overflow-hidden border border-slate-100 shadow-stripe-card aspect-video animate-in zoom-in-95 duration-700 bg-slate-50">
            <iframe 
              title="City Map" 
              width="100%" 
              height="100%" 
              style={{ border: 0 }} 
              src={`https://maps.google.com/maps?q=${encodeURIComponent(msg.cityForMap + ', USA')}&t=&z=12&ie=UTF8&iwloc=&output=embed&hl=${language === 'zh' ? 'zh-CN' : 'en'}`}
            />
          </div>
        )}

        {parts.length > 1 && (
          <div className="w-full mt-4">
            {renderTextContent(parts.slice(1).join('\n'))}
          </div>
        )}

        {msg.groundingLinks && msg.groundingLinks.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2">
            {msg.groundingLinks.map((link, lIdx) => (
              <a key={lIdx} href={link.uri} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3.5 py-1.5 bg-slate-50 border border-slate-100 rounded-stripe text-[12px] text-primary font-bold hover:bg-primary hover:text-white transition-all shadow-sm">
                <span className="material-symbols-outlined text-[14px]">link</span>
                <span className="max-w-[140px] truncate">{link.title}</span>
              </a>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`flex flex-col h-screen w-full max-w-2xl mx-auto bg-white overflow-hidden font-google-sans-text ${isRTL ? 'rtl' : 'ltr'}`}>
      <header className="flex-none px-6 pt-10 pb-6 flex items-center justify-between border-b border-slate-50">
        <button onClick={onBack} className="p-2 -ml-2 rounded-stripe hover:bg-slate-50 transition-all border border-transparent">
          <span className={`material-symbols-outlined text-slate-soft text-[24px] ${isRTL ? 'rotate-180' : ''}`}>arrow_back</span>
        </button>
        <button 
          onClick={onHome}
          className="text-center transition-opacity hover:opacity-80 active:scale-95"
        >
          <div className="text-[18px] font-black text-slate-dark tracking-tight font-google-sans uppercase">Settlo AI</div>
        </button>
        <div className="w-10"></div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-8 space-y-14 no-scrollbar bg-white scroll-smooth pb-40">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'} w-full`}>
            <div className={`${msg.role === 'user' ? 'max-w-[85%]' : 'w-full'} transition-all duration-500 animate-in fade-in slide-in-from-bottom-4`}>
              {msg.role === 'user' ? (
                <div className="px-5 py-3.5 rounded-stripe bg-m3-secondary-container text-slate-dark font-[700] shadow-sm">
                  <p className="leading-relaxed text-[15px]">{msg.content}</p>
                </div>
              ) : (
                renderAssistantMessage(msg)
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2 px-4 py-2">
            <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce"></div>
            <div className="w-1.5 h-1.5 bg-primary/60 rounded-full animate-bounce [animation-delay:0.2s]"></div>
            <div className="w-1.5 h-1.5 bg-primary/30 rounded-full animate-bounce [animation-delay:0.4s]"></div>
          </div>
        )}
      </div>

      <div className="p-6 bg-white border-t border-slate-50 flex flex-col gap-5">
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2.5 animate-in slide-in-from-bottom-2 duration-300">
            {suggestions.map((s, sIdx) => (
              <button 
                key={sIdx} 
                onClick={() => handleSend(s)} 
                className="px-4 py-2 bg-white border border-slate-200 rounded-stripe text-[13px] font-bold text-slate-soft hover:border-primary hover:text-primary hover:bg-slate-50 transition-all active:scale-95 shadow-stripe-card"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="max-w-2xl mx-auto relative w-full">
          <div className="group relative flex items-center bg-slate-50 rounded-stripe p-1.5 transition-all duration-300 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary/5 border border-transparent focus-within:border-primary/10">
            <input 
              type="text" 
              value={input} 
              onChange={(e) => setInput(e.target.value)} 
              onKeyDown={(e) => e.key === 'Enter' && handleSend(input)} 
              placeholder="" 
              className={`flex-1 bg-transparent border-none focus:ring-0 ${isRTL ? 'text-right pr-5' : 'text-left pl-5'} py-4 text-[16px] text-slate-dark outline-none font-medium`} 
            />
            <button 
              onClick={() => handleSend(input)} 
              disabled={!input.trim() || loading} 
              className={`flex items-center justify-center w-12 h-12 bg-primary text-white rounded-stripe disabled:bg-slate-200 transition-all shadow-lg active:scale-95 mx-1`}
            >
              <span className="material-symbols-outlined text-[20px]">north</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiAgentPage;
